import { ArrowRight, Mic } from 'lucide-react'
import { Link } from 'react-router-dom'
import { Button } from '@/components/ui'
import type { HelpSource } from '@/services/helpFeedback/appFeedbackApi'
import { cn } from '@/utils'
import { hasSpoken, type Card, type Conversation, type Turn } from './conversation'
import { AnswerMarkdown } from './AnswerMarkdown'
import { SummaryCard } from './SummaryCard'
import {
  ANSWER_CHIPS, ASK_ANOTHER_WAY, CHIP_WORDS, DID_IT_SORT, FAILURE_CHIPS, FROM_SOURCE, GO_TO_SOURCE, NOT_QUITE, PAGE_CHIPS,
  SEND, SHOW_ME, SOURCES_LABEL, SPEAKER_HELP, SPEAKER_YOU, THINKING, YES,
  failureOpening, openingLine, type ChipKey,
} from './helpFeedbackText'
import type { ChipId } from '@/services/feedbackContext/chips'
import { useHelpFeedbackStore } from '@/stores/helpFeedbackStore'
import { usePageTour } from '@/hooks/usePageTour'
import { SHOW_ME_AROUND } from '@/components/tour/tourText'
import { usePanelControls } from './helpFeedbackContext'
import { useFeatureStore } from '@/stores/featureStore'
import { usePermissionStore } from '@/stores/permissionStore'
import { mayPlay, taskTourById } from '@/services/tour/taskTours'

export interface TurnHandlers {
  chip: (key: ChipKey) => void
  resolved: () => void
  notQuite: () => void
  editCard: (patch: Partial<Card>) => void
  removeChip: (chip: ChipId) => void
  send: () => void
  addMore: () => void
  /** The not-found offer's two buttons (SA-1314). */
  sendQuestion: () => void
  askAnotherWay: () => void
}

function Speaker({ who }: { who: string }) {
  return <span className="sr-only">{who}: </span>
}

const CHIP_CLASS =
  'rounded-full border border-border bg-background px-3 py-1 text-sm outline-none transition-colors hover:bg-accent focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50'

/** The page's tour, offered beside the page chips when the page has one (SA-1315). */
function ShowMeAround() {
  const tour = usePageTour()
  const { startTour } = usePanelControls()
  if (!tour) return null
  return (
    <button type="button" onClick={() => startTour(tour)} className={CHIP_CLASS} data-testid="help-feedback-tour-chip">
      {SHOW_ME_AROUND}
    </button>
  )
}

/**
 * The answer's task tour (SA-1316 spec §7): offered when this bundle has it and the viewer may open
 * every page it visits, read strictly, so nothing shows while either store loads.
 */
function ShowMe({ tourId, turnId }: { tourId?: string; turnId: number }) {
  const { startTour } = usePanelControls()
  const featuresLoaded = useFeatureStore((s) => s.isLoaded)
  const features = useFeatureStore((s) => s.features)
  const permissionsLoaded = usePermissionStore((s) => s.isLoaded)
  const permissions = usePermissionStore((s) => s.permissions)
  const tour = taskTourById(tourId)
  if (!tour || !featuresLoaded || !permissionsLoaded || !mayPlay(tour, features, permissions)) return null
  return (
    <Button size="sm" onClick={() => startTour(tour, turnId)} data-testid="help-feedback-show-me">
      {SHOW_ME}
    </Button>
  )
}

function AssistantLine({ children }: { children: React.ReactNode }) {
  return <div className="max-w-[92%] text-sm leading-relaxed">{children}</div>
}

/**
 * The help an answer read, under it (SA-1314): a help page as "From: <title>", an app page as
 * "Go to <title>", a template step as its words. Links route in the app, so the panel stays open.
 */
function Sources({ sources }: { sources: HelpSource[] }) {
  return (
    <div className="mt-2 text-xs text-muted-foreground" data-testid="help-feedback-sources">
      <p className="sr-only">{SOURCES_LABEL}</p>
      <ul className="flex flex-wrap gap-x-3 gap-y-1">
        {sources.map((s) => (
          <li key={s.slug}>
            {s.kind === 'map' && s.href ? (
              <Link to={s.href} className="inline-flex items-center gap-1 font-medium text-primary hover:underline">
                {GO_TO_SOURCE} {s.title}
                <ArrowRight aria-hidden className="size-3" />
              </Link>
            ) : (s.kind === 'guide' || s.kind === 'page') && s.href ? (
              <>
                {FROM_SOURCE}:{' '}
                <Link to={s.href} className="text-primary hover:underline">{s.title}</Link>
              </>
            ) : (
              <span>{s.kind === 'step' ? s.title : `${FROM_SOURCE}: ${s.title}`}</span>
            )}
          </li>
        ))}
      </ul>
    </div>
  )
}

/** Whether "Did that sort it?" belongs under this answer: the newest, settled, nothing after it. */
function asksIfSorted(c: Conversation, index: number): boolean {
  const t = c.turns[index]
  if (t.type !== 'answer' || t.streaming || t.resolved || c.guidanceSent || c.sent) return false
  return !c.turns.slice(index + 1).some((n) => n.type === 'answer' || n.type === 'card' || n.type === 'notFound')
}

/** The conversation as the person reads it (decisions 7 to 13). */
export function Turns({ c, viewAs, handlers }: { c: Conversation; viewAs: boolean; handlers: TurnHandlers }) {
  const opening = c.opener.failure ? failureOpening(c.opener.failure.message) : openingLine(c.page)
  // With answers off (SA-1306 §3) the chips that ask for an answer are not offered.
  const answers = useHelpFeedbackStore((s) => s.answers)
  const chips = (c.opener.failure ? FAILURE_CHIPS : PAGE_CHIPS).filter((key) => answers || !ANSWER_CHIPS.includes(key))
  const last = c.turns[c.turns.length - 1]
  const thinking = c.waiting === 'turn' && !(last?.type === 'answer' && last.streaming)

  const render = (t: Turn, i: number) => {
    switch (t.type) {
      case 'person':
        return (
          <div key={t.id} className="ml-auto flex max-w-[85%] items-start gap-1.5 rounded-lg bg-muted px-3 py-2 text-sm whitespace-pre-wrap">
            <Speaker who={SPEAKER_YOU} />
            <span className="min-w-0">{t.text}</span>
            {t.spoken && <Mic aria-hidden className="mt-0.5 size-3 shrink-0 text-muted-foreground" />}
          </div>
        )
      case 'assistant':
        return (
          <AssistantLine key={t.id}>
            <Speaker who={SPEAKER_HELP} />
            {t.text && <p>{t.text}</p>}
            {t.question && <p className={cn('font-medium', t.text && 'mt-1')}>{t.question}</p>}
          </AssistantLine>
        )
      case 'answer':
        return (
          <AssistantLine key={t.id}>
            <Speaker who={SPEAKER_HELP} />
            <AnswerMarkdown content={t.markdown} />
            {!t.streaming && !t.spoken && t.sources && t.sources.length > 0 && <Sources sources={t.sources} />}
            {asksIfSorted(c, i) && (
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <span className="text-xs text-muted-foreground">{DID_IT_SORT}</span>
                <ShowMe tourId={t.tour} turnId={t.id} />
                <Button size="sm" variant="outline" onClick={handlers.resolved} disabled={viewAs} data-help-yes={t.id}>{YES}</Button>
                <Button size="sm" variant="ghost" onClick={handlers.notQuite}>{NOT_QUITE}</Button>
              </div>
            )}
          </AssistantLine>
        )
      case 'notFound':
        return (
          <AssistantLine key={t.id}>
            <Speaker who={SPEAKER_HELP} />
            <p>{t.text}</p>
            {!t.settled && !c.sent && (
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <Button size="sm" onClick={handlers.sendQuestion} disabled={viewAs || c.waiting !== 'none'}>{SEND}</Button>
                <Button size="sm" variant="ghost" onClick={handlers.askAnotherWay} disabled={c.waiting !== 'none'}>{ASK_ANOTHER_WAY}</Button>
              </div>
            )}
          </AssistantLine>
        )
      case 'card':
        return (
          <SummaryCard
            key={t.id}
            lead={t.lead}
            card={t.card}
            live={!t.dimmed && !c.sent}
            snapshot={c.snapshot}
            removed={c.removed}
            sending={c.waiting === 'send'}
            disabled={viewAs || c.waiting !== 'none'}
            onEdit={handlers.editCard}
            onRemoveChip={handlers.removeChip}
            onSend={handlers.send}
            onAddMore={handlers.addMore}
          />
        )
    }
  }

  return (
    <div className="space-y-4">
      <AssistantLine>
        <Speaker who={SPEAKER_HELP} />
        <p>{opening}</p>
      </AssistantLine>
      {!hasSpoken(c) && (
        <div className="flex flex-wrap gap-2">
          {chips.map((key) => (
            <button
              key={key}
              type="button"
              disabled={viewAs}
              onClick={() => handlers.chip(key)}
              className={CHIP_CLASS}
            >
              {CHIP_WORDS[key]}
            </button>
          ))}
          {!c.opener.failure && <ShowMeAround />}
        </div>
      )}
      {c.turns.map(render)}
      {thinking && (
        <p className="flex items-center gap-1.5 text-sm text-muted-foreground" data-testid="help-feedback-thinking">
          {THINKING}
          <span aria-hidden className="flex gap-0.5">
            <span className="size-1 animate-pulse rounded-full bg-current" />
            <span className="size-1 animate-pulse rounded-full bg-current [animation-delay:150ms]" />
            <span className="size-1 animate-pulse rounded-full bg-current [animation-delay:300ms]" />
          </span>
        </p>
      )}
    </div>
  )
}
